import type { FC } from 'react';
import { cn } from '@r/utils/utils.ts';
import { useCoreWorkspaceConfigFlag } from '@r/hooks/use-core-workspace-config-flag.ts';
import '../../assets/styles/partials/marquee.css';

interface MarqueeProps {
  text: string | null;
  className?: string;
}

// Répétitions par moitié de piste — hérité de l'ancienne implémentation
// (suffisant pour couvrir la largeur viewport aux plus grandes tailles de texte).
const REPEAT = 10;
// Espace insécable : évite tout rognage / collapse au raccord des deux moitiés.
const NBSP = String.fromCharCode(0xa0);

/**
 * Bandeau décoratif : le texte (nom du workspace / de la boutique) défile en
 * très gros, en filigrane (`opacity-15`), sur toute la largeur du viewport
 * (`full-bleed`).
 *
 * Deux moitiés de piste identiques ; la piste translate de -50 % (une moitié) en
 * boucle linéaire → défilement sans couture, animé par `transform` (composité
 * GPU) et désactivé sous `prefers-reduced-motion`. Purement décoratif →
 * `aria-hidden`.
 */
const Marquee: FC<MarqueeProps> = ({ text, className }) => {
  const isSliderTitleHidden = useCoreWorkspaceConfigFlag('hide-slider-title');

  if (!text || isSliderTitleHidden) {
    return null;
  }

  const half = `${Array.from({ length: REPEAT }, () => text).join(NBSP)}${NBSP}`;

  return (
    <div
      className={cn('full-bleed pointer-events-none overflow-hidden', className)}
      aria-hidden="true"
    >
      <div className="marquee overflow-hidden font-bold text-foreground opacity-15">
        <div className="marquee__track w-max whitespace-nowrap font-bold">
          <span className="inline-block text-[5rem] font-bold xl:text-[12rem]">{half}</span>
          <span className="inline-block text-[5rem] font-bold xl:text-[12rem]">{half}</span>
        </div>
      </div>
    </div>
  );
};

export default Marquee;
